import { Badge, IconButton } from "@compass/core";
import { HubConnectionState } from "@microsoft/signalr";
import PhonelinkRingIcon from '@mui/icons-material/PhonelinkRing';
import { TextingConversationsHubAction } from "generated/openApi";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { textingConversationsPageRoute } from "pages/texting/TextingConversationsPage/TextingConversationsPage";
import { TEXTING_UNREAD_COUNT_CHANGED_EVENT } from "pages/texting/textingEvents";
import { useTextingConversationsHubConnection } from "pages/texting/useTextingConversationsHubConnection";
import ApiService from "services/apiService";

export const AppTopBarTexting = () => {
  const [textingEnabled, setTextingEnabled] = useState(false);

  useEffect(() => {
    ApiService.textingConversations
      .getTenantTextingInfo()
      .then((tenantInfo) => setTextingEnabled(tenantInfo.canInfobip))
      .catch((error) => console.error("Failed to determine whether tenant texting is enabled:", error));
  }, []);

  return textingEnabled ? <AppTopBarTextingButton /> : null;
};

const AppTopBarTextingButton = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();
  const { textingConversationsHubConnection, connectionStatus } = useTextingConversationsHubConnection();

  const fetchUnreadCount = useCallback(async () => {
    try {
      const count = await ApiService.textingConversations.getUnreadMessageCount();
      setUnreadCount(count);
    } catch (error) {
      console.error("Failed to fetch unread text message count:", error);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (connectionStatus === HubConnectionState.Connected) {
      fetchUnreadCount();
    }
  }, [connectionStatus, fetchUnreadCount]);

  useEffect(() => {
    window.addEventListener(TEXTING_UNREAD_COUNT_CHANGED_EVENT, fetchUnreadCount);

    return () => {
      window.removeEventListener(TEXTING_UNREAD_COUNT_CHANGED_EVENT, fetchUnreadCount);
    };
  }, [fetchUnreadCount]);

  useEffect(() => {
    const refreshUnreadCount = (_incomingJsonString: string) => {
      fetchUnreadCount();
    };

    textingConversationsHubConnection.on(
      TextingConversationsHubAction[TextingConversationsHubAction.BroadcastConversationUpdate],
      refreshUnreadCount
    );

    return () => {
      textingConversationsHubConnection.off(
        TextingConversationsHubAction[TextingConversationsHubAction.BroadcastConversationUpdate],
        refreshUnreadCount
      );
    };
  }, [fetchUnreadCount, textingConversationsHubConnection]);

  return (
    <IconButton
      onClick={() => navigate(`/${textingConversationsPageRoute.getPath()}`)}
      aria-label={`Open texting conversations${unreadCount > 0 ? `, ${unreadCount} unread messages` : ""}`}
      color="inherit"
    >
      <Badge badgeContent={unreadCount} color="error" invisible={unreadCount === 0}>
        <PhonelinkRingIcon color="action" />
      </Badge>
    </IconButton>
  );
};
